/*import { Dimens2D, PhraseType } from 'Phaser/Scenes/SightReading';
import PlayablePhrase from '../Containers/PlayablePhrase';
import CursorBase from 'Phaser/GameObjects/CursorBase';
import {
  DefaultConfig,
  MediumConfig,
  MinimumConfig,
  PhraseGraphic,
  PhraseData,
} from 'Phaser/GameObjects/StaffLine';
import { SizingProps } from 'Phaser/GameObjects/StaffLine/Types';
import { ResizeParams, optimizeStaffLineHeight } from 'Phaser/GameObjects/StaffLine/Util';
import Logger from 'Utils/Logger';
import LessonCursor from 'Phaser/GameObjects/LessonCursor';
import { EventStreamPhaser2, IteratorExtensible } from 'Phaser/GameObjects/CursorMobile';
import { INoteGraphicsDatabase } from 'Phaser/Scenes/StaffExercise';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';*/

import CursorMobile, {
  EventStreamPhaser2,
  IteratorExtensible,
} from 'Phaser/GameObjects/CursorMobile';
import {
  DefaultConfig,
  MediumConfig,
  MinimumConfig,
  PhraseData,
  PhraseGraphic,
} from 'Phaser/GameObjects/StaffLine';
import { CursorData, SizingProps } from 'Phaser/GameObjects/StaffLine/Types';
import {
  getBraceWidth,
  ResizeParams,
  optimizeStaffLineHeight,
} from 'Phaser/GameObjects/StaffLine/Util';
import { PlayablePhrase as _PlayablePhrase } from 'Phaser/Scenes/SightReadingPhaser';
import { INoteGraphicsDatabase } from 'Phaser/Scenes/StaffExercise';
import Logger from 'Utils/Logger';
import Phrase, { Iterator } from 'Models/Phrase';
import { ReactToPhaserListeners, RemoveListener } from 'Phaser/EventBus2';
import { transformSerializedIteratorPitches } from 'Utils/Transposition';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import { getCircularQueueIndices } from './CircularQueue';

export type RequestParams = {
  excludeFirstNoteRest: boolean;
  /** Monotonic id for this request. The producer echoes it back on PhraseReady
   *  so the manager can restore request order for the initial fill. */
  sequenceId: number;
};

interface LayoutConfig {
  layoutDirection: PhraseLayout;
  minPaddingX: number;
  minPaddingY: number;
}

export enum PhraseLayout {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

/** Game pixels between viewport left and first-phrase brace left on mobile
 *  (world distance = this / camera zoom). */
const MOBILE_BRACE_VIEWPORT_GAP_PX = 14;

const defaultConfig: LayoutConfig = {
  layoutDirection: PhraseLayout.Vertical,
  minPaddingX: 30,
  minPaddingY: 10,
};

type PhraseManagerConfig = {
  mobile?: boolean;
  maxPhrasesVisible?: number;
  onPhraseRequested?: (params: RequestParams) => void;
  layoutConfig?: LayoutConfig;
  eventStream?: EventStreamPhaser2;
  // Gameplay callbacks driven by each cursor; supplied by the scene and
  // forwarded to every CursorMobile the manager creates.
  onPlayingLastSlice?: (start: number, duration: number, durationInSecs: number) => void;
  onFinishPlayingPhrase?: (eligible?: boolean) => void;
  onStartPlayingMeasure?: (globalMeasure: number, phraseLocalMeasure: number) => void;
  // Invoked when a phrase is evicted from the active queue (for cleanup).
  onPhraseRemoved?: (uuid: string) => void;
  // Score-theme colors applied to phrases/cursors the manager creates.
  notationColor?: number;
  staffLineColor?: number;
  cursorColor?: number;
  // Initial sight-reading transposition applied to every phrase the manager
  // builds (updated live via setTranspose).
  transposeSemitones?: number;
  modeConversion?: ParallelModeConversion;
};

export interface IPhraseData {
  measureCount: number;
}

type PhraseType = PhraseGraphic;
interface CursorBase {}
type PlayablePhrase<T1, T2> = _PlayablePhrase;
interface Dimens2D {
  width: number;
  height: number;
}

interface SceneWithPhrases extends INoteGraphicsDatabase {
  onStartPlayingMeasure: (globalMeasure: number, phraseLocalMeasure: number) => void;
}

type SceneType = SceneWithPhrases & Phaser.Scene;

export default abstract class PhraseManager<
  PhraseDataType extends IPhraseData,
  PhraseGraphic extends PhraseType,
  Cursor extends CursorBase,
> {
  scene: SceneType;
  layoutConfig: LayoutConfig;
  maxPhrasesVisible?: number;

  // Active phrases live in a circular buffer indexed by [queueStart, queueEnd).
  phrases: (PlayablePhrase<PhraseGraphic, Cursor> | undefined)[] = [];
  protected queueStart?: number;
  protected queueEnd?: number;
  protected queueSize: number = 0;
  protected queueCapacity: number;

  // Pre-fetched phrase data buffered before being rendered.
  protected phraseLoadQueue: PhraseDataType[] = [];
  protected pendingRequests: number = 0;
  private firstRequest: boolean = true;
  /** Monotonic counter assigned to each outgoing phrase request. The producer
   *  echoes the id back on PhraseReady so out-of-order responses can be
   *  reordered into request order during the initial fill. */
  protected requestSequenceCounter: number = 0;
  private onPhraseRequested?: (params: RequestParams) => void;
  /** Phrase indices whose end has already fired a network request. Dedupes
   *  the produce side against cursor rollback / re-entry into the same phrase. */
  protected requestedForPhraseIndices: Set<number> = new Set();
  /** Phrase indices whose measure-2 has already consumed a buffered phrase
   *  into the active queue. Dedupes the consume side against rollback. */
  protected consumedForPhraseIndices: Set<number> = new Set();

  // Scene-supplied gameplay callbacks (chevrons, adaptive tempo, etc.) routed
  // from each cursor through the manager.
  protected onPlayingLastSliceCb?: (
    start: number,
    duration: number,
    durationInSecs: number,
  ) => void;
  protected onFinishPlayingPhraseCb?: (eligible?: boolean) => void;
  protected onStartPlayingMeasureCb?: (globalMeasure: number, phraseLocalMeasure: number) => void;
  protected onPhraseRemovedCb?: (uuid: string) => void;

  // Score-theme colors (applied when creating phrases/cursors).
  protected notationColor?: number;
  protected staffLineColor?: number;
  protected cursorColor?: number;

  // Current sight-reading transposition (applied when building each phrase).
  protected transposeSemitones: number = 0;
  protected modeConversion: ParallelModeConversion = 'off';

  activePhrase?: number;
  mostRecentAddedPhrase?: PhraseGraphic;

  visiblePhraseCount: number = 0;
  totalPhrasesAdded: number = 0;
  // Number of total measures added to the scene so far
  measureCount: number = 0;
  mobile?: boolean;
  mainCursor?: Cursor;

  canvasDimens: Dimens2D;

  constructor(scene: SceneType, canvasDimens: Dimens2D, config?: PhraseManagerConfig) {
    this.layoutConfig = config?.layoutConfig ?? defaultConfig;
    this.maxPhrasesVisible = config?.maxPhrasesVisible;
    this.mobile = config?.mobile;
    this.scene = scene;
    this.canvasDimens = canvasDimens;
    this.queueCapacity = config?.maxPhrasesVisible ?? Infinity;
    this.onPhraseRequested = config?.onPhraseRequested;
    this.onPlayingLastSliceCb = config?.onPlayingLastSlice;
    this.onFinishPlayingPhraseCb = config?.onFinishPlayingPhrase;
    this.onStartPlayingMeasureCb = config?.onStartPlayingMeasure;
    this.onPhraseRemovedCb = config?.onPhraseRemoved;
    this.notationColor = config?.notationColor;
    this.staffLineColor = config?.staffLineColor;
    this.cursorColor = config?.cursorColor;
    this.transposeSemitones = config?.transposeSemitones ?? 0;
    this.modeConversion = config?.modeConversion ?? 'off';
  }

  pausePlayback() {
    this.phrases.forEach((phrase) => {
      phrase?.pause();
    });
  }

  destroy() {}

  // ---------------------------------------------------------------------------
  // Phrase data flow: producer → load queue → render
  // ---------------------------------------------------------------------------

  /** Producer delivers phrase data here. Renders immediately when a request
   *  is pending; otherwise buffers in the load queue. */
  enqueuePhraseData(data: PhraseDataType) {
    this.phraseLoadQueue.push(data);
    console.log(
      `%c[PhraseManagement] ← RESOLVED%c active=${this.queueSize} buffered=${this.phraseLoadQueue.length} pending=${this.pendingRequests}`,
      'color:#1b8a3f;font-weight:bold',
      'color:inherit',
    );
    if (this.pendingRequests > 0) {
      const next = this.phraseLoadQueue.shift()!;
      this.addPhrase(next);
      this.pendingRequests--;
    }
  }

  clearPhraseQueue() {
    this.phraseLoadQueue = [];
    this.pendingRequests = 0;
  }

  /** Pull the oldest buffered phrase data; undefined if the load queue is empty. */
  protected getNextAvailablePhraseData(): PhraseDataType | undefined {
    return this.phraseLoadQueue.shift();
  }

  /** Notify the producer that another phrase is needed. */
  requestPhrase() {
    console.log(
      `%c[PhraseManagement] → REQUEST%c active=${this.queueSize} buffered=${this.phraseLoadQueue.length} pending=${this.pendingRequests} firstRequest=${this.firstRequest}`,
      'color:#b8860b;font-weight:bold',
      'color:inherit',
    );
    this.onPhraseRequested?.({
      excludeFirstNoteRest: this.firstRequest,
      sequenceId: this.requestSequenceCounter++,
    });
    this.firstRequest = false;
  }

  /** Render the next buffered phrase if available; otherwise request one. */
  protected consumeOrRequestNextPhrase() {
    const next = this.getNextAvailablePhraseData();
    if (next !== undefined) {
      this.addPhrase(next);
    } else {
      this.requestPhrase();
      this.pendingRequests++;
    }
  }

  /** Pull the next buffered phrase into the active queue. If the buffer is
   *  empty (network slower than playback), mark a deferred consume so the
   *  arrival auto-renders via `enqueuePhraseData`. Used by the measure-2
   *  trigger; the matching network request fires at phrase-end. */
  protected consumeBufferedPhrase() {
    const next = this.getNextAvailablePhraseData();
    if (next !== undefined) {
      this.addPhrase(next);
    } else {
      this.pendingRequests++;
    }
  }

  // ---------------------------------------------------------------------------
  // Playback lifecycle hooks
  // ---------------------------------------------------------------------------

  onFinishPlayingPhrase() {
    this.consumeOrRequestNextPhrase();
  }

  // ---------------------------------------------------------------------------
  // Active phrase queue (circular buffer)
  // ---------------------------------------------------------------------------

  addPhrase(phraseData: PhraseDataType) {
    const phrase = this.createPhrase(phraseData);
    this.mostRecentAddedPhrase = phrase.graphic as PhraseGraphic;
    this.totalPhrasesAdded++;
    this.measureCount += phraseData.measureCount;
    this.addPhraseToActiveQueue(phrase);
  }

  abstract createPhrase(phraseData: PhraseDataType): PlayablePhrase<PhraseGraphic, Cursor>;

  protected addPhraseToActiveQueue(phrase: PlayablePhrase<PhraseGraphic, Cursor>) {
    if (this.queueSize >= this.queueCapacity) {
      this.removeOldestPhrase();
    }
    if (this.queueStart === undefined || this.queueEnd === undefined) {
      this.queueStart = 0;
      this.queueEnd = 0;
    }
    this.phrases[this.queueEnd] = phrase;
    this.queueEnd = (this.queueEnd + 1) % this.queueCapacity;
    this.queueSize++;
    this.visiblePhraseCount++;
  }

  protected removeOldestPhrase() {
    if (this.queueStart === undefined) return;
    const oldest = this.phrases[this.queueStart];
    console.debug(
      `[phrase-destroy] PhraseManager.removeOldestPhrase slot=${this.queueStart} uuid=${oldest?.graphic._data.uuid} currentCount=${this.queueSize} maxAllowed=${this.queueCapacity} totalAdded=${this.totalPhrasesAdded}`,
    );
    if (oldest) this.onPhraseRemovedCb?.(oldest.graphic._data.uuid);
    oldest?.destroy();
    this.phrases[this.queueStart] = undefined;
    this.queueStart = (this.queueStart + 1) % this.queueCapacity;
    this.queueSize--;
    this.visiblePhraseCount--;
  }

  // ---------------------------------------------------------------------------
  // Queue iteration / lookup
  // ---------------------------------------------------------------------------

  getFirstPhrase(): PlayablePhrase<PhraseGraphic, Cursor> | undefined {
    if (this.queueStart === undefined) return undefined;
    return this.phrases[this.queueStart];
  }

  getLastPhrase(): PlayablePhrase<PhraseGraphic, Cursor> | undefined {
    if (this.queueEnd === undefined) return undefined;
    const idx = (this.queueEnd - 1 + this.queueCapacity) % this.queueCapacity;
    return this.phrases[idx];
  }

  getActivePhrase(): PlayablePhrase<PhraseGraphic, Cursor> | undefined {
    if (this.activePhrase === undefined) return undefined;
    return this.phrases[this.activePhrase];
  }

  /** Index of the slot following the active phrase, wrapping by queue capacity.
   *  Replaces the scene's old hard-coded `(activePhrase + 1) % 4`. */
  getNextIndex(): number {
    const cap = Number.isFinite(this.queueCapacity) ? this.queueCapacity : this.phrases.length || 1;
    return ((this.activePhrase ?? 0) + 1) % cap;
  }

  getNextPhrase(): PlayablePhrase<PhraseGraphic, Cursor> | undefined {
    return this.phrases[this.getNextIndex()];
  }

  findPhraseContainingMeasure(
    measure: number,
  ): [number, PlayablePhrase<PhraseGraphic, Cursor>] | undefined {
    for (const i of getCircularQueueIndices(this.queueStart, this.queueSize, this.queueCapacity)) {
      const phrase = this.phrases[i];
      if (phrase?.graphic.containsMeasure(measure)) return [i, phrase];
    }
    return undefined;
  }

  // ---------------------------------------------------------------------------
  // Active phrase navigation
  // ---------------------------------------------------------------------------

  activateNextPhrase() {
    if (this.activePhrase === this.queueEnd) {
      Logger.warn('[PhraseManager] No next phrase to activate');
      return;
    }
    const next = this.activePhrase === undefined ? 0 : (this.activePhrase + 1) % this.queueCapacity;
    if (!this.phrases[next]) {
      Logger.warn('[PhraseManager] No next phrase to activate');
      return;
    }
    this.activatePhrase(next);
  }

  activatePreviousPhrase() {
    if (this.activePhrase === this.queueStart) {
      Logger.warn('[PhraseManager] No previous phrase to activate');
      return;
    }
    const prev =
      this.activePhrase === undefined
        ? 0
        : (this.activePhrase - 1 + this.queueCapacity) % this.queueCapacity;
    if (!this.phrases[prev]) {
      Logger.warn('[PhraseManager] No previous phrase to activate');
      return;
    }
    this.activatePhrase(prev);
  }

  activatePhrase(idx: number) {
    if (this.activePhrase !== undefined) this.phrases[this.activePhrase]?.deactivate();
    this.phrases[idx]?.activate(true);
    this.activePhrase = idx;
  }

  activatePhraseByMeasure(measure: number) {
    const result = this.findPhraseContainingMeasure(measure);
    if (result !== undefined) {
      this.activatePhrase(result[0]);
    }
  }

  // ---------------------------------------------------------------------------
  // Frame update / resize
  // ---------------------------------------------------------------------------

  update(_delta: number) {
    this.phrases.forEach((phrase) => {
      phrase?.update();
    });
  }

  getViewportSize() {
    const cam = this.scene.cameras.main;
    return { width: cam.width, height: cam.height };
  }

  resize(dimens: Dimens2D) {
    if (dimens === this.canvasDimens) return;
    this.canvasDimens = dimens;
    this.onResize(dimens);
  }

  abstract onResize(dimens: Dimens2D): void;
}

export class PhraseManagerMuseflowRenderer extends PhraseManager<
  PhraseData,
  PhraseGraphic,
  CursorMobile
> {
  playbackStarted: boolean = false;
  lastLayout?: ResizeParams;
  eventStream!: EventStreamPhaser2;
  /** Lesson zoom ceiling, recomputed from the layout during onResize. The scene
   *  reads this when running its zoom tween. */
  maxZoomLevel: number = 1;

  /** When true, cursors are created render-hidden so the cursor-visibility
   *  toggle (off) is respected from the first frame — including phrases created
   *  later during gameplay. The scene keeps this in sync via setCursorVisible. */
  cursorHidden: boolean = false;

  /** Running clock used to assign each phrase a startTimestamp at queue time.
   *  Advanced by the full measure-duration of every phrase as it enters the
   *  active queue, guaranteeing phrases are timestamped strictly in order. */
  private accumulatingTimestamp: number = 0;

  /** Count of initial phrases still awaited in request order. While > 0, the
   *  manager is in its "init" phase and reorders out-of-order responses. */
  private initialPhrasesRemaining: number = 0;
  /** The request sequence id the init phase is waiting to render next. */
  private nextInitSequenceId: number = 0;
  /** Initial responses that arrived ahead of their turn, kept sorted ascending
   *  by sequenceId until the gap before them is filled. */
  private initBacklog: { sequenceId: number; phrase: Phrase }[] = [];

  constructor(scene: SceneType, canvasDimens: Dimens2D, config?: PhraseManagerConfig) {
    super(scene, canvasDimens, config);
    if (config?.eventStream) this.eventStream = config.eventStream;
    ReactToPhaserListeners.PhraseReady(this.onPhraseReady, this);
  }

  destroy() {
    console.debug(
      `[phrase-destroy] PhraseManagerMuseflowRenderer.destroy currentCount=${this.queueSize} maxAllowed=${this.queueCapacity} totalAdded=${this.totalPhrasesAdded}`,
    );
    RemoveListener('PhraseReady');
    this.phrases.forEach((phrase) => phrase?.destroy());
    this.mainCursor?.destroy();
    this.mainCursor = undefined;
  }

  /** Kick off the initial request-driven fill so the visible queue is populated
   *  before playback. The producer (React) answers each request via PhraseReady. */
  requestInitialPhrases() {
    const count = this.mobile ? 3 : 2;
    // Enter the init phase: these `count` requests fire back-to-back here and
    // (on a fresh manager) take sequence ids 0..count-1. Their responses may
    // resolve out of order, so onPhraseReady reorders them by sequenceId before
    // rendering, guaranteeing the initial on-screen phrases match request order.
    this.initialPhrasesRemaining = count;
    this.nextInitSequenceId = this.requestSequenceCounter;
    this.initBacklog = [];
    for (let i = 0; i < count; i++) {
      this.consumeOrRequestNextPhrase();
    }
  }

  /** Producer (React) hands us a freshly fetched, untransformed phrase. We
   *  build the PhraseData here (applying the current transposition) and
   *  buffer/render it through the queue.
   *
   *  During the init phase the initial phrases must enter the queue in the
   *  order they were requested. Responses can arrive out of order, so any
   *  phrase that isn't the next-expected sequence id is parked in a sorted
   *  backlog; once the gap is filled we drain the backlog in order. */
  private onPhraseReady = ({ phrase, sequenceId }: { phrase: Phrase; sequenceId: number }) => {
    if (this.initialPhrasesRemaining > 0) {
      // Insert keeping initBacklog sorted ascending by sequenceId.
      const insertAt = this.initBacklog.findIndex((e) => e.sequenceId > sequenceId);
      const entry = { sequenceId, phrase };
      if (insertAt === -1) this.initBacklog.push(entry);
      else this.initBacklog.splice(insertAt, 0, entry);

      // Drain every contiguous run starting at the next-expected sequence id.
      while (
        this.initialPhrasesRemaining > 0 &&
        this.initBacklog[0]?.sequenceId === this.nextInitSequenceId
      ) {
        const next = this.initBacklog.shift()!;
        this.nextInitSequenceId++;
        this.initialPhrasesRemaining--;
        this.enqueuePhraseData(this.buildPhraseData(next.phrase, next.sequenceId));
      }
      return;
    }

    this.enqueuePhraseData(this.buildPhraseData(phrase, sequenceId));
  };

  /** Build a PhraseData from a raw Phrase, applying the manager's current
   *  transposition / mode-conversion to the iterator. */
  private buildPhraseData(phrase: Phrase, sequenceId?: number): PhraseData {
    const raw = phrase.getMeasureData();
    const cursorData: CursorData = {
      serializedIterator: transformSerializedIteratorPitches(
        raw.serializedIterator,
        this.transposeSemitones,
        this.modeConversion,
        phrase.musicXML.xml,
      ),
      measureRects: raw.measureRects,
    };
    const phraseData = new PhraseData(
      phrase,
      this.totalPhrasesAdded,
      cursorData,
      phrase.uuid,
      phrase.musicXML.timeSignatures[0],
      true, // showTimeSig — overridden per layout in createPhrase
      true, // showBrace
      false, // endMeasure
      true, // showClef
      0, // startTimestamp — assigned at queue time in addPhrase
    );
    phraseData.sequenceId = sequenceId;
    return phraseData;
  }

  /** Live transpose: re-transpose the on-screen phrases in place. Rebuilds the
   *  active queue from the same Phrase objects (so timestamps/layout are
   *  identical) and re-populates the gameplay event stream. */
  setTranspose(semitones: number, modeConversion: ParallelModeConversion): number {
    if (semitones === this.transposeSemitones && modeConversion === this.modeConversion) {
      return this.queueSize;
    }
    this.transposeSemitones = semitones;
    this.modeConversion = modeConversion;

    // Capture the currently-queued phrases in play order before tearing down.
    const activePhrases: Phrase[] = [];
    for (const i of getCircularQueueIndices(this.queueStart, this.queueSize, this.queueCapacity)) {
      const phrase = this.phrases[i]?.graphic._data.phrase;
      if (phrase) activePhrases.push(phrase);
    }
    if (activePhrases.length === 0) return 0;

    // Tear down current rendering + the (now stale) gameplay events, then reset
    // all queue/clock bookkeeping so the rebuild reproduces identical positions.
    console.debug(
      `[phrase-destroy] PhraseManagerMuseflowRenderer.setTranspose teardown currentCount=${this.queueSize} maxAllowed=${this.queueCapacity} totalAdded=${this.totalPhrasesAdded} semitones=${semitones}`,
    );
    this.phrases.forEach((p) => p?.destroy());
    this.mainCursor?.destroy();
    this.mainCursor = undefined;
    this.eventStream?.reset();
    this.phrases = [];
    this.queueStart = undefined;
    this.queueEnd = undefined;
    this.queueSize = 0;
    this.visiblePhraseCount = 0;
    this.totalPhrasesAdded = 0;
    this.measureCount = 0;
    this.accumulatingTimestamp = 0;
    this.activePhrase = 0;
    this.mostRecentAddedPhrase = undefined;
    this.requestedForPhraseIndices.clear();
    this.consumedForPhraseIndices.clear();

    activePhrases.forEach((phrase) => this.addPhrase(this.buildPhraseData(phrase)));

    // Reposition (desktop layout is driven by onResize) and snap the cursor home.
    this.onResize(this.canvasDimens);
    this.getFirstPhrase()?.activate(false, true);
    return activePhrases.length;
  }

  /** Assign the phrase its timestamp from the running clock, register its time
   *  signature with the timekeeper, then advance the clock by the phrase's full
   *  duration so the next phrase starts exactly where this one ends. */
  addPhrase(phraseData: PhraseData) {
    console.debug(
      `[phrase-seq] RENDER seq=${phraseData.sequenceId} uuid=${phraseData.uuid} order=${this.totalPhrasesAdded}`,
    );
    const timeSignature = phraseData.timeSignature;
    phraseData.startTimestamp = this.accumulatingTimestamp;
    // PhraseData copies startTimestamp into trebleClef/bassClef at construction
    // time. We construct PhraseData with a placeholder (0) and assign the real
    // value here, so the clef copies need to be synced too — otherwise
    // Clef.getNotePosition's `note.timestamp + clef.startTimestamp === target`
    // check uses 0 for every phrase past the first, and lookups silently miss.
    phraseData.trebleClef.startTimestamp = this.accumulatingTimestamp;
    phraseData.bassClef.startTimestamp = this.accumulatingTimestamp;
    phraseData.phrase.setStartTimestamp(this.accumulatingTimestamp);
    console.debug(
      `[phrase-add] uuid=${phraseData.uuid} startTs=${phraseData.startTimestamp} measures=${phraseData.measureCount} timeSig=${timeSignature.numerator}/${timeSignature.denominator} totalAdded=${this.totalPhrasesAdded} currentCount=${this.queueSize}`,
    );
    // measureCount here is the cumulative measure index before this phrase.
    this.eventStream?.timeKeeper.setTimeSignatures(this.measureCount, [timeSignature]);
    super.addPhrase(phraseData);
    const measureDuration = timeSignature.numerator / timeSignature.denominator;
    this.accumulatingTimestamp += phraseData.measureCount * measureDuration;
    this.eventStream?.addEvents2(
      new Iterator(phraseData.serializedIterator, phraseData.phrase),
      phraseData.startTimestamp,
    );
    // Desktop: re-layout all visible phrases now that a new one is in the queue
    // (createPhrase only positions horizontal/mobile layouts in positionPhrase).
    if (!this.mobile) this.onResize(this.canvasDimens);
  }

  createPhrase(phraseData: PhraseData): PlayablePhrase<PhraseGraphic, CursorMobile> {
    const isFirstPhrase = this.phrases.length === 0;
    const horizontal = this.layoutConfig.layoutDirection === PhraseLayout.Horizontal;
    const drawSymbols = !horizontal || isFirstPhrase;

    phraseData.showClef = drawSymbols;
    phraseData.showBrace = drawSymbols;
    // The time signature belongs only on the first phrase (matching the OSMD and
    // EventBus2 scenes). Repeating it on every stacked line reserved time-sig
    // leading space on lines that don't render one, pushing their first notes in
    // as far as the first line's. Clef/brace still repeat per line as expected.
    phraseData.showTimeSig = isFirstPhrase;

    const dpr = window.devicePixelRatio;
    const phraseGraphic = new PhraseGraphic(
      this.scene,
      0,
      {
        globalScaling: 1,
        pitchLineGap: 20,
        pitchLineWidth: 1 * dpr,
        measureLineWidth: 2 * dpr,
        firstNoteGap: horizontal ? 40 : undefined,
        clefGap: 100,
        clefSymbolMargin: horizontal ? 10 : undefined,
        doubleEndLines: false,
        notationColor: this.notationColor,
        staffLineColor: this.staffLineColor,
      },
      phraseData,
      'Fade',
      this.measureCount,
      phraseData.startTimestamp,
      this.totalPhrasesAdded % 2 === 1,
    );

    let cursor: CursorMobile | undefined;
    // Cursor to pass to positionPhrase for sizing/placement. Differs from the
    // cursor stored on the PlayablePhrase on mobile, where mainCursor is shared
    // across phrases (and would be double-destroyed if a phrase referenced it).
    let cursorToPosition: CursorMobile | undefined;
    if (horizontal) {
      if (this.mainCursor === undefined) {
        // First mobile phrase: create the shared cursor and size it here.
        this.mainCursor = this.createCursor(phraseData, isFirstPhrase);
        cursorToPosition = this.mainCursor;
      } else {
        // Subsequent mobile phrases: extend the shared cursor's iterator so it
        // knows how to play past the end of the previous phrase. Without this
        // the iterator stops once it crosses into a phrase it has no
        // phraseInfo entry for.
        this.mainCursor.addPhrase(
          phraseData.serializedIterator,
          phraseData.uuid,
          phraseData.timeSignature,
          this.totalPhrasesAdded,
        );
        console.debug('[phrase-add] extending shared cursor', this.mainCursor?.iterator);
      }
    } else {
      cursor = this.createCursor(phraseData, isFirstPhrase);
      cursorToPosition = cursor;
    }

    this.positionPhrase(phraseGraphic, cursorToPosition);

    return new _PlayablePhrase(phraseGraphic, cursor);
  }

  private createCursor(phraseData: PhraseData, isFirstPhrase: boolean): CursorMobile {
    const sceneAsDb = this.scene;
    // Captured at creation so the manager's request-gating can dedupe per phrase.
    const phraseIndex = this.totalPhrasesAdded;
    // Each cursor callback drives BOTH the manager's own request/bookkeeping
    // logic and the scene-supplied gameplay callback. Arrow functions keep the
    // manager as `this` even though CursorMobile invokes them via .call/.bind.
    const onLastSlice = (start: number, duration: number, durationInSecs: number) => {
      this.onPlayingLastSliceCb?.(start, duration, durationInSecs);
    };
    const onFinishPlaying = (eligible?: boolean) => {
      // Phrase complete fires the network REQUEST for the next phrase. The
      // arrival is buffered, not rendered; the matching consume at
      // measure-2 of the next phrase performs the queue insertion. Firing
      // the request this early (vs. the prior measure-2-of-current trigger)
      // gives the network a full extra phrase of lead time, important at
      // high tempo (250+ bpm) where a slow response would otherwise miss
      // the consume window. Dedupe per phrase index so cursor rollback /
      // re-entry into the same phrase doesn't double-request.
      const cursorPhraseIdx = this.mainCursor?.getEffectivePhraseIndex() ?? phraseIndex;
      if (!this.requestedForPhraseIndices.has(cursorPhraseIdx)) {
        this.requestedForPhraseIndices.add(cursorPhraseIdx);
        this.requestPhrase();
      }
      this.onFinishPlayingPhraseCb?.(eligible);
    };
    const onStartPlayingMeasure = (globalMeasure: number, phraseLocalMeasure: number) => {
      // Measure 2 (phrase-local) CONSUMES the phrase that was requested at
      // the prior phrase's end — pulls it from the buffer and inserts into
      // the active queue. If the buffer is empty (network slow), the
      // consume is deferred via `pendingRequests` and the next arrival
      // auto-renders.
      //
      // Desktop skips phrase 0: phrase 0's end is the very first request
      // trigger, so there's nothing in the buffer to consume yet, and the
      // queue already holds phrase 0 + phrase 1 (cap=2) from initial preload.
      // Dedupe per phrase index against cursor rollback.
      if (phraseLocalMeasure === 2) {
        const cursorPhraseIdx = this.mainCursor?.getEffectivePhraseIndex() ?? phraseIndex;
        const skipForDesktopPhraseZero = !this.mobile && cursorPhraseIdx === 0;
        if (!skipForDesktopPhraseZero && !this.consumedForPhraseIndices.has(cursorPhraseIdx)) {
          this.consumedForPhraseIndices.add(cursorPhraseIdx);
          this.consumeBufferedPhrase();
        }
      }
      this.onStartPlayingMeasureCb?.(globalMeasure, phraseLocalMeasure);
    };
    // Start with an empty iterator; cursor.addPhrase below populates phraseInfo
    // at the correct index so phraseInfo[iterator.phraseIndex] resolves. (If we
    // pass serializedIterator to the constructor it pushes phraseInfo[0] regardless
    // of which phrase this is, which breaks every phrase past the first.)
    const lessonCursor = new CursorMobile(
      sceneAsDb,
      0,
      0,
      0,
      0,
      this.eventStream,
      new IteratorExtensible(
        sceneAsDb,
        [],
        phraseData.phrase,
        phraseData.timeSignature,
        undefined,
        undefined,
        phraseIndex,
      ),
      phraseData.serializedIterator,
      onLastSlice,
      onFinishPlaying,
      undefined,
      // Non-first phrases start invisible — `phrase.activate()` calls
      // `cursor.enable()` which restores the visible alpha once it's that
      // phrase's turn to play. Without this the pre-rendered upcoming
      // phrase's cursor sits visibly at (0, phraseY) at scene load.
      !isFirstPhrase,
      phraseData.startTimestamp,
      phraseIndex,
      isFirstPhrase,
      onStartPlayingMeasure,
      undefined,
      this.cursorColor,
    );
    // Mirror the old scene's reset + addPhrase: gives the iterator its
    // startingMeasure / timestampOffset, then populates phraseInfo[phraseIndex].
    lessonCursor.reset(
      new IteratorExtensible(
        sceneAsDb,
        [],
        phraseData.phrase,
        phraseData.timeSignature,
        undefined,
        undefined,
        phraseIndex,
      ),
      phraseData.startTimestamp,
      this.measureCount,
    );
    lessonCursor.addPhrase(
      phraseData.serializedIterator,
      phraseData.uuid,
      phraseData.timeSignature,
      phraseIndex,
    );
    // Seed the global cursor-toggle state on every cursor as it's created, so a
    // cursor born during gameplay/scroll honors the current toggle immediately
    // (and never flashes before the next SetCursorVisible event arrives).
    lessonCursor.setCursorVisibility(!this.cursorHidden);
    return lessonCursor;
  }

  // ---------------------------------------------------------------------------
  // Mobile camera scroll-follow (manager-driven; the cursor stays passive)
  // ---------------------------------------------------------------------------

  /** The first phrase's brace, if it's on screen and visible. The mobile
   *  camera follow uses this as the spatial reference for paddingLeft. */
  private getMobileFirstPhraseBrace(): Phaser.GameObjects.Image | undefined {
    if (!this.mobile || !this.mainCursor?.isAtLessonIteratorStart()) return undefined;
    const graphic =
      this.phrases.find((p) => p?.graphic._data.startTimestamp === 0)?.graphic ??
      this.phrases.find((p) => p !== undefined)?.graphic;
    if (!graphic?._data.showBrace) return undefined;
    const brace = graphic.brace;
    return brace && brace.visible ? brace : undefined;
  }

  /**
   * Mobile left-edge anchor for the CameraController: the brace's world-x
   * position and the *screen-pixel* gap that should be held between
   * viewport-left and the brace. The controller derives `paddingLeft` from
   * these each frame so the gap stays fixed across zoom changes.
   *
   * Returns undefined until the first-phrase brace exists and the shared
   * cursor is at the lesson's start (so the world position can be captured
   * cleanly). The scene retries each frame until this succeeds.
   */
  getMobileLeftAnchor(): { worldX: number; screenGap: number } | undefined {
    if (!this.mobile) return undefined;
    const brace = this.getMobileFirstPhraseBrace();
    if (!brace) return undefined;
    return { worldX: brace.x, screenGap: MOBILE_BRACE_VIEWPORT_GAP_PX };
  }

  /** Recompute the lesson zoom ceiling from the current layout. Moved here from
   *  the scene so it runs as part of onResize, right after the phrases are laid
   *  out — the scene then reads {@link maxZoomLevel} when applying its zoom. */
  updateMaxZoomLevel(paddingHorizontal: number, paddingVertical: number, braceWidth: number): void {
    const cam = this.scene.cameras.main;
    const vpWidth = cam.width;
    const vpHeight = cam.height;
    const extra = 5 * window.devicePixelRatio;
    const phraseWidth = vpWidth - (paddingHorizontal - braceWidth - extra) * 2;
    const phraseHeight = vpHeight - (paddingVertical - extra) * 2;
    this.maxZoomLevel = this.mobile
      ? vpHeight / phraseHeight
      : Math.min(vpWidth / phraseWidth, vpHeight / phraseHeight);
  }

  /** Desktop staff-sizing bounds (matches the legacy two-stave layout). */
  private desktopSizingProps(): { minProps: SizingProps; maxProps: SizingProps } {
    const maxProps: SizingProps = {
      minPaddingX: 125,
      minPaddingY: 50,
      idealPhraseConfig: DefaultConfig({}),
      minPhraseConfig: MediumConfig(),
      minStaffSpacingVertical: 35,
      idealPaddingX: 150,
      idealPaddingY: 75,
    };
    const minProps: SizingProps = {
      minPaddingX: 125,
      minPaddingY: 30,
      idealPhraseConfig: maxProps.minPhraseConfig,
      minPhraseConfig: MinimumConfig(),
      idealPaddingX: maxProps.minPaddingX,
      idealPaddingY: maxProps.minPaddingY,
      minStaffSpacingVertical: 20,
    };
    return { minProps, maxProps };
  }

  /** Mobile staff-sizing bounds (tighter horizontal padding for the scroll
   *  view). Mirrors what positionPhrase used to compute inline. */
  private mobileSizingProps(): { minProps: SizingProps; maxProps: SizingProps } {
    const maxProps: SizingProps = {
      minPaddingX: 125,
      minPaddingY: 30,
      idealPhraseConfig: DefaultConfig({}),
      minPhraseConfig: MediumConfig(),
      minStaffSpacingVertical: 35,
      idealPaddingX: 150,
      idealPaddingY: 75,
    };
    const minProps: SizingProps = {
      minPaddingX: 30,
      minPaddingY: 10,
      idealPhraseConfig: maxProps.minPhraseConfig,
      minPhraseConfig: MinimumConfig(),
      idealPaddingX: maxProps.minPaddingX,
      idealPaddingY: maxProps.minPaddingY,
      minStaffSpacingVertical: 0,
    };
    return { minProps, maxProps };
  }

  onResize({ width, height }: Dimens2D) {
    const minAspectRatio = 4.5;
    const maxAspectRatio = 8;
    // Compute the layout reference for the current mode and store it. Mobile
    // doesn't fire resize naturally, so the scene calls this manually at init
    // to give positionPhrase something to read from for the first phrase.
    const { minProps, maxProps } = this.mobile
      ? this.mobileSizingProps()
      : this.desktopSizingProps();
    const layout = optimizeStaffLineHeight(
      [width, height],
      { min: minProps, max: maxProps },
      minAspectRatio,
      maxAspectRatio,
      this.mobile ?? false,
    );
    this.lastLayout = layout;
    if (!layout.fit) {
      Logger.warn(
        '[PhraseManagerMuseflowRenderer::resize] not enough space for staff lines! Falling back to minimum configuration',
      );
    }

    // Mobile positions phrases as they're added (in positionPhrase); the only
    // thing onResize needs to do is store the reference layout above.
    if (this.mobile) return;

    const hasAnyPhrase = this.phrases.some((p) => p !== undefined);
    if (!hasAnyPhrase) return;

    const { paddingX, paddingY, phraseRenderingConfig, staffSpacingVertical, phraseDimens } =
      layout;
    const { topClefNumExtraLinesTop, innerHeight } = phraseDimens;
    const minWidth = minAspectRatio * innerHeight;
    const availableWidth = Math.max(minWidth, width - paddingX * 2);
    const dpr = window.devicePixelRatio;
    // Recompute the zoom ceiling from this layout before the scene re-applies zoom.
    const braceWidth = getBraceWidth(phraseDimens, this.scene) * dpr;
    this.updateMaxZoomLevel(paddingX * dpr, paddingY * dpr, braceWidth);
    this.phrases.forEach((p) => {
      if (p === undefined) return;
      const phraseHeightTotal =
        phraseDimens.topClefHeight.outerHeight +
        phraseDimens.bottomClefHeight.outerHeight +
        phraseRenderingConfig.clefGap +
        1 * phraseRenderingConfig.pitchLineGap;
      const yOffset = topClefNumExtraLinesTop * 0.5 * phraseRenderingConfig.pitchLineGap;
      const phraseY =
        paddingY + yOffset + (p.graphic.bottom ? phraseHeightTotal + staffSpacingVertical : 0);
      p.graphic.resize(
        phraseRenderingConfig,
        availableWidth,
        paddingX,
        phraseY,
        phraseDimens,
        innerHeight,
      );
      const c = p.cursor;
      if (c === undefined) return;
      c.height = innerHeight * 1.25 * dpr;
      c.width = c.height / 10;
      c.radius = c.width / 2;
      const y = (phraseY + innerHeight / 2) * dpr - c.height / 2;
      c.setY(y);
      if (!this.playbackStarted && c.idx === 0) {
        const firstNotePhrase =
          (this.activePhrase !== undefined ? this.phrases[this.activePhrase] : undefined) ??
          this.phrases.find((ph) => ph !== undefined);
        const firstNote = firstNotePhrase?.graphic.getFirstNotePosition();
        if (firstNote !== undefined) {
          // `lastNotePos` is the cursor's center (matches `it.left()` and the
          // lerp in `calcPos`). `setX` is the left edge, so subtract width/2 for
          // the visual placement. Storing `firstNote` (not `firstNote − w/2`)
          // in lastNotePos avoids a half-width jump when the first update runs.
          c.setLastNotePos(firstNote);
          c.setX(firstNote - c.width / 2);
        }
      }
    });
  }

  positionPhrase(phraseGraphic: PhraseGraphic, cursor?: CursorMobile): void {
    if (this.layoutConfig.layoutDirection === PhraseLayout.Horizontal) {
      const nextPhrasePosition = this.mostRecentAddedPhrase?.getNextPosition() ?? 0;
      // The layout reference is computed once per resize and reused for every
      // phrase added in this mode. If no resize has run yet, fall back to a
      // one-shot compute so we never read off a missing layout.
      if (!this.lastLayout) this.onResize(this.canvasDimens);
      if (!this.lastLayout) return;
      const { phraseDimens, phraseRenderingConfig } = this.lastLayout;
      const { innerHeight } = phraseDimens;
      const dpr = window.devicePixelRatio;
      const width = innerHeight * 4.5;
      phraseGraphic.resize(phraseRenderingConfig, width, 0, 0, phraseDimens, innerHeight);
      // Vertically center the phrase using its bounding box height and camera's view height,
      // since phraseDimens doesn't account for devicePixelRatio (DPR).
      const camHeight = this.getViewportSize().height;
      const centeredY = (camHeight - innerHeight * dpr) / 2;
      phraseGraphic.setY(centeredY);
      phraseGraphic.setX(nextPhrasePosition);
      if (cursor) {
        cursor.height = innerHeight * 1.25 * dpr;
        cursor.width = cursor.height / 10;
        cursor.radius = cursor.width / 2;
        // centeredY is already in canvas units (computed from cam.height), so
        // only innerHeight needs the dpr conversion. The desktop path differs:
        // there phraseY is CSS and the whole sum is scaled.
        const cursorY = centeredY + (innerHeight * dpr) / 2 - cursor.height / 2;
        cursor.setY(cursorY);
        const cursorX = phraseGraphic.getFirstNotePosition();
        cursor.setStartPosition(cursorX);
      }
    }
  }
}
